import { cn } from '@/utils';
import {useEffect, useState, useMemo} from "react";
import Tile from '../../molecules/Tile';

const STORAGE_PREFIX = 'tileList:reduced:';

const TileList = ({
  tileClassName,
  headline,
  items,
  onItemClick,
  parentId,
  loadingIndex,
  LoadingIcon,
  showIcon,
  isDisabled,
  Icon,
  showLabel,
  linkText,
  onLinkClick,
  id,
  getItemDataAttributes = () => ({}),
  dynamicItemClassNames = () => [],
  reduceItems = false,
  reduceItemsText = '',
  expandItemsText = '',
  isShape = false,
  initialSelectId = '',
  instances = [],
  persistReduceState = false,
}) => {
  const handleLinkClick = () => {
    onLinkClick();
  };

  const [itemsReduced, setItemsReduced] = useState(() => {
    if (persistReduceState && id) {
      try {
        const stored = sessionStorage.getItem(STORAGE_PREFIX + id);
        if (stored !== null) return stored === 'true';
      } catch (e) { /* storage unavailable */ }
    }
    return reduceItems === true;
  });
  const [initialSelectIdState, setInitialSelectIdState] = useState(initialSelectId);

  const selectedInstanceId = useMemo(() =>
      instances.find(item => item.is_selected)?.id ?? null,
    [instances]
  );

  const [initialInstanceId, setInitialInstanceId] = useState(selectedInstanceId);

  // This useEffect is relevant to trigger the sorting of the sortedItems
  useEffect(() => {
    // Because we need to wait for the async fetch when other
    // instance is being fetched we look for "isDisabled" false
    if (!isDisabled && initialInstanceId !== selectedInstanceId) {
      // We check initialInstanceId !== selectedInstanceId
      // because we only want to look for instance changes and
      // trigger the sorting afterwards
      setInitialInstanceId(selectedInstanceId);
      setInitialSelectIdState(initialSelectId);
    }
  }, [isDisabled, selectedInstanceId]);

  const sortedItems = initialSelectIdState
    ? [...items].sort((a, b) => {
      if (a.id === initialSelectIdState) return -1;
      if (b.id === initialSelectIdState) return 1;
      return 0;
    })
    : items;

  const selectedIndex = sortedItems.findIndex(item => item.is_selected === true);

  const handleToggleReduce = () => {
    const next = !itemsReduced;
    setItemsReduced(next);
    if (persistReduceState && id) {
      try {
        sessionStorage.setItem(STORAGE_PREFIX + id, String(next));
      } catch (e) { /* storage unavailable */ }
    }
  };

  return (
    <div id={id} className="mb-2xl">
      <div className="flex justify-between items-center mb-2xs">
        <label className="block font-semibold text-sm desktop:text-base">
          {headline}
        </label>
        <If condition={linkText}>
          <button
            className="bg-transparent border-none text-[color:var(--theme-link-text-color-2)] font-bold inline-flex items-center whitespace-nowrap gap-4xs"
            onClick={handleLinkClick}
          >
            <ft-icon name="info" width={20} height={20}></ft-icon>
            <span className="text-xs desktop:text-sm">{linkText}</span>
          </button>
        </If>
      </div>
      <div className="flex flex-wrap -m-3xs">
        {sortedItems.map((item, i) => {
          const { label, is_selected, id, orgIndex } = item;
          const dataAttributes = getItemDataAttributes(item);
          return (
            <Tile
              key={orgIndex ?? id ?? i}
              index={i}
              orgIndex={orgIndex}
              id={id}
              parentId={parentId}
              isSelected={is_selected}
              className={cn(
                tileClassName,
                ...dynamicItemClassNames({i, selectedIndex, itemsReduced }),
              )}
              onClick={onItemClick}
              LoadingIcon={LoadingIcon}
              loadingIndex={loadingIndex}
              {...{ isDisabled, dataAttributes }}
            >
              <Choose>
                <When condition={showIcon && Icon}>
                  <Icon id={id} label={label} showLabel={showLabel}/>
                </When>
                <Otherwise>{label}</Otherwise>
              </Choose>
            </Tile>
          );
        })}
      </div>
      <If condition={reduceItems}>
        <div className={cn(
          "flex items-center align-right cursor-pointer",
          isShape && items.length < 8 ? "tablet:hidden" : "",
          isShape && items.length <= 10 ? "laptop:hidden" : "",
        )} onClick={handleToggleReduce}>
          <button
            className="bg-transparent border-none text-[color:var(--theme-link-text-color-2)] font-bold inline-flex items-center whitespace-nowrap gap-4xs mt-4xs ">
            <Choose>
              <When condition={itemsReduced}>
                <ft-icon name="caret-down" width="20" height="20"></ft-icon>
                <span className="text-xs desktop:text-sm">{expandItemsText}</span>
              </When>
              <Otherwise>
                <ft-icon name="caret-up" width="20" height="20"></ft-icon>
                <span className="text-xs desktop:text-sm">{reduceItemsText}</span>
              </Otherwise>
            </Choose>
          </button>
        </div>
      </If>
    </div>
  );
};

export default TileList;
